import { FormEvent, useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { EventType } from "../types";
import { blankEvent, defaultSchedule } from "../config/schedule";
import { AdminShell } from "../components/admin/AdminShell";
import { Toast } from "../components/Toast";
import { useConfirm } from "../hooks/useConfirm";
import { useToast } from "../hooks/useToast";
import { durationLabel } from "../utils/date";
import { weekdaySummary } from "../utils/events";

export function AdminPage() {
  const [events, setEvents] = useState<EventType[]>([]);
  const [editing, setEditing] = useState<Partial<EventType>>(blankEvent);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState("");
  const { toast, showToast } = useToast();
  const { askConfirm, confirmModal } = useConfirm();

  const refresh = useCallback(() => {
    api.getEvents(true).then((items) => {
      setEvents(items);
      if (!selectedEventId && items[0]) setSelectedEventId(items[0].id);
    });
  }, [selectedEventId]);

  useEffect(refresh, [refresh]);

  async function saveEvent(e: FormEvent) {
    e.preventDefault();
    if (editing.id) {
      await api.updateEvent(editing.id, editing);
      showToast("Event шинэчлэгдлээ.");
    } else {
      await api.createEvent({ ...editing, schedule: editing.schedule || defaultSchedule });
      showToast("Event үүслээ.");
    }
    setEditing(blankEvent);
    setEventModalOpen(false);
    refresh();
  }

  return (
    <AdminShell active="events" title="Event төрөл, өдөр цагийн тохиргоо">
      <section className="admin-layout">
        <div className="admin-card wide">
          <div className="admin-card-title">
            <h2>Event жагсаалт</h2>
            <button type="button" onClick={() => {
              setEditing(blankEvent);
              setEventModalOpen(true);
            }}>
              + New Event
            </button>
          </div>
          <div className="admin-event-list">
            {events.map((event) => (
              <article className={!event.enabled ? "admin-event-row disabled" : "admin-event-row"} key={event.id}>
                <span style={{ background: event.enabled ? event.color : undefined }} />
                <button type="button" onClick={() => setSelectedEventId(event.id)}>
                  <strong>{event.title}</strong>
                  <small>
                    {durationLabel(event.durationMinutes)} • {event.meetingType} • {weekdaySummary(event.schedule)}, hours vary
                  </small>
                </button>
                <button type="button" onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/book/${event.id}`)}>
                  Copy link
                </button>
                <button type="button" onClick={() => patchSelectedFor(event, { enabled: !event.enabled })}>
                  {event.enabled ? "Turn Off" : "Turn On"}
                </button>
                <button type="button" onClick={() => {
                  setEditing(event);
                  setEventModalOpen(true);
                }}>
                  Засах
                </button>
                <button className="danger-button" type="button" onClick={() => deleteEvent(event)}>
                  Устгах
                </button>
              </article>
            ))}
          </div>
        </div>

      </section>

      {eventModalOpen && (
        <div className="modal-backdrop" role="presentation">
          <form className="event-modal" onSubmit={saveEvent}>
            <div className="modal-title">
              <h2>{editing.id ? "Event засах" : "Event үүсгэх"}</h2>
              <button type="button" onClick={() => setEventModalOpen(false)}>
                ×
              </button>
            </div>
            <input placeholder="Гарчиг" required value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            <textarea
              placeholder="Тайлбар"
              value={editing.description || ""}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
            />
            <select
              value={editing.category || "regular"}
              onChange={(e) => setEditing({ ...editing, category: e.target.value as EventType["category"] })}
            >
              <option value="regular">Энгийн багц</option>
              <option value="portrait">Хөрөг зургийн багц</option>
            </select>
            <div className="two-col">
              <input
                type="number"
                placeholder="Минут"
                value={editing.durationMinutes || 30}
                onChange={(e) => setEditing({ ...editing, durationMinutes: Number(e.target.value) })}
              />
              <input
                type="number"
                placeholder="Үнэ"
                value={editing.price || 0}
                onChange={(e) => setEditing({ ...editing, price: Number(e.target.value) })}
              />
            </div>
            <div className="two-col">
              <input type="color" value={editing.color || "#006bff"} onChange={(e) => setEditing({ ...editing, color: e.target.value })} />
              <label className="toggle-line">
                <input
                  type="checkbox"
                  checked={editing.enabled ?? true}
                  onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                />
                Идэвхтэй
              </label>
            </div>
            <button className="primary-action" type="submit">
              Хадгалах
            </button>
          </form>
        </div>
      )}
      {confirmModal}
      <Toast message={toast} />
    </AdminShell>
  );

  async function patchSelectedFor(event: EventType, patch: Partial<EventType>) {
    await api.updateEvent(event.id, patch);
    showToast("Event тохиргоо шинэчлэгдлээ.");
    refresh();
  }

  function deleteEvent(event: EventType) {
    askConfirm({
      title: "Event устгах уу?",
      message: `${event.title} event болон холбоотой захиалгууд устах болно.`,
      confirmLabel: "Устгах",
      onConfirm: async () => {
        await api.deleteEvent(event.id);
        showToast("Event устгагдлаа.");
        refresh();
      },
    });
  }
}
